import { useCallback, useEffect, useMemo, useState } from 'react';
import { Preloader } from 'konsta/react';
import { ChevronDown, SlidersHorizontal } from 'lucide-react';
import { api, type DiscoverFilters, type DiscoverResult, type ProviderCard } from '../../api';
import { ProviderPost, ProviderTile, StoriesRow } from '../../components/ProviderPost';
import { BottomSheet, EmptyState, PrimaryButton } from '../../components/ui';
import { inputClass } from '../../components/form';
import { ProviderProfile } from '../ProviderProfile';
import { FeedbackCards, PromoBanner } from '../Growth';
import { useNav } from '../../nav';
import { useT } from '../../i18n';
import { haptic } from '../../telegram';

const RADII = [2, 5, 10, 25];

/** Loads providers page by page; reloads when filters change. */
function useProviders(filters: DiscoverFilters) {
  const [data, setData] = useState<DiscoverResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const key = JSON.stringify(filters);

  const load = useCallback(
    async (offset = 0) => {
      setLoading(true);
      setFailed(false);
      try {
        const r = await api.providers({ ...filters, offset });
        setData((d) => (offset && d ? { ...r, items: [...d.items, ...r.items] } : r));
      } catch {
        setFailed(true);
      } finally {
        setLoading(false);
      }
    },
    [key],
  );
  useEffect(() => {
    load(0);
  }, [load]);
  return { data, loading, failed, reload: () => load(0), more: () => data?.nextOffset && load(data.nextOffset) };
}

function useOpenProfile(canBook: boolean, onChanged?: () => void) {
  const nav = useNav();
  return (p: ProviderCard) => nav.push(`provider:${p.id}`, <ProviderProfile id={p.id} initial={p} canBook={canBook} onChanged={onChanged} />);
}

/** Client home: "Available now" row and an Instagram-style feed, nearest first. */
export function HomeTab() {
  const t = useT();
  const { data, loading, failed, reload, more } = useProviders({});
  const open = useOpenProfile(true, reload);
  const recent = useMemo(
    () =>
      (data?.items ?? [])
        .filter((p) => p.availableSince && Date.now() - Date.parse(p.availableSince) < 6 * 3600_000)
        .slice(0, 12),
    [data],
  );

  if (!data && loading) return <Loading />;
  if (failed && !data) return <EmptyState icon="📡" text={t('error.generic')} action={<RetryButton onClick={reload} />} />;
  return (
    <div className="bg-tg-secondary pb-28">
      <PromoBanner />
      <FeedbackCards />
      <StoriesRow items={recent} onOpen={open} />
      {data?.mode === 'city' && <p className="bg-tg-bg px-4 py-2 text-sm text-tg-hint">{t('feed.no_location')}</p>}
      {data && !data.items.length && <EmptyState icon="🔍" text={t('feed.empty')} />}
      <div className="pt-2">
        {data?.items.map((p) => (
          <ProviderPost key={p.id} p={p} canBook onOpen={() => open(p)} />
        ))}
      </div>
      {data?.nextOffset !== null && data?.nextOffset !== undefined && <LoadMore loading={loading} onClick={more} />}
    </div>
  );
}

/** Search with filter chips and a two-column grid. */
export function SearchTab() {
  const t = useT();
  const [filters, setFilters] = useState<DiscoverFilters>({ radiusKm: 10 });
  const [sheet, setSheet] = useState(false);
  const { data, loading, reload, more } = useProviders(filters);
  const open = useOpenProfile(true, reload);

  const chips: Array<{ label: string; active: boolean }> = [
    { label: filters.radiusKm ? `≤ ${filters.radiusKm} km` : `${t('filter.distance')}: ${t('filter.any_distance')}`, active: !!filters.radiusKm },
    { label: filters.gender ? t(`gender.${filters.gender}`) : t('filter.gender'), active: !!filters.gender },
    { label: filters.minutes ? t('provider.min', { minutes: filters.minutes }) : t('filter.session'), active: !!filters.minutes },
    { label: filters.maxPrice ? `≤ ${filters.maxPrice} ETB` : t('filter.max_price'), active: !!filters.maxPrice },
    { label: filters.minRating ? `★ ${filters.minRating}+` : t('filter.rating'), active: !!filters.minRating },
  ];

  return (
    <div className="pb-28">
      <div className="no-scrollbar sticky top-0 z-10 flex gap-2 overflow-x-auto bg-tg-bg px-3 py-2.5">
        <button onClick={() => setSheet(true)} className="flex shrink-0 items-center gap-1.5 rounded-full bg-tg-button px-3.5 py-2 text-sm font-semibold text-tg-buttonText">
          <SlidersHorizontal size={16} /> {t('filter.title')}
        </button>
        {chips.map((c) => (
          <button
            key={c.label}
            onClick={() => setSheet(true)}
            className={`flex shrink-0 items-center gap-1 rounded-full px-3.5 py-2 text-sm ${c.active ? 'bg-tg-text text-tg-bg' : 'bg-tg-secondary'}`}
          >
            {c.label} <ChevronDown size={14} />
          </button>
        ))}
      </div>
      {data && <p className="px-4 pb-2 text-sm text-tg-hint">{t('filter.results', { n: data.total })}</p>}
      {!data && loading && <Loading />}
      {data && !data.items.length && <EmptyState icon="🔍" text={t('feed.empty')} />}
      <div className="grid grid-cols-2 gap-2 px-3">
        {data?.items.map((p) => (
          <ProviderTile key={p.id} p={p} onOpen={() => open(p)} />
        ))}
      </div>
      {data?.nextOffset !== null && data?.nextOffset !== undefined && <LoadMore loading={loading} onClick={more} />}
      <FilterSheet open={sheet} value={filters} onClose={() => setSheet(false)} onApply={(f) => (setFilters(f), setSheet(false))} />
    </div>
  );
}

function FilterSheet({ open, value, onClose, onApply }: { open: boolean; value: DiscoverFilters; onClose: () => void; onApply: (f: DiscoverFilters) => void }) {
  const t = useT();
  const [f, setF] = useState<DiscoverFilters>(value);
  useEffect(() => {
    if (open) setF(value);
  }, [open]);
  const Pill = ({ on, label, onClick }: { on: boolean; label: string; onClick: () => void }) => (
    <button
      onClick={() => {
        haptic('select');
        onClick();
      }}
      className={`rounded-full px-4 py-2 text-sm font-medium ${on ? 'bg-tg-button text-tg-buttonText' : 'bg-tg-secondary'}`}
    >
      {label}
    </button>
  );
  const Row = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <div className="mb-5">
      <div className="mb-2 text-sm font-semibold">{title}</div>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
  return (
    <BottomSheet open={open} onClose={onClose}>
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-xl font-bold">{t('filter.title')}</h3>
        <button className="text-tg-link" onClick={() => setF({ radiusKm: 10 })}>
          {t('filter.clear')}
        </button>
      </div>
      <Row title={t('filter.distance')}>
        <Pill on={!f.radiusKm} label={t('filter.any_distance')} onClick={() => setF({ ...f, radiusKm: 0 })} />
        {RADII.map((r) => (
          <Pill key={r} on={f.radiusKm === r} label={`${r} km`} onClick={() => setF({ ...f, radiusKm: r })} />
        ))}
      </Row>
      <Row title={t('filter.gender')}>
        <Pill on={!f.gender} label={t('filter.any')} onClick={() => setF({ ...f, gender: undefined })} />
        <Pill on={f.gender === 'FEMALE'} label={t('gender.FEMALE')} onClick={() => setF({ ...f, gender: 'FEMALE' })} />
        <Pill on={f.gender === 'MALE'} label={t('gender.MALE')} onClick={() => setF({ ...f, gender: 'MALE' })} />
      </Row>
      <Row title={t('filter.session')}>
        <Pill on={!f.minutes} label={t('filter.any')} onClick={() => setF({ ...f, minutes: undefined })} />
        {[60, 120].map((m) => (
          <Pill key={m} on={f.minutes === m} label={t('provider.min', { minutes: m })} onClick={() => setF({ ...f, minutes: m })} />
        ))}
      </Row>
      <Row title={t('filter.rating')}>
        <Pill on={!f.minRating} label={t('filter.any')} onClick={() => setF({ ...f, minRating: undefined })} />
        {[3, 4, 4.5].map((r) => (
          <Pill key={r} on={f.minRating === r} label={`★ ${r}+`} onClick={() => setF({ ...f, minRating: r })} />
        ))}
      </Row>
      <div className="mb-5">
        <div className="mb-2 text-sm font-semibold">{t('filter.max_price')}</div>
        <input
          className={inputClass}
          type="number"
          inputMode="numeric"
          placeholder="—"
          value={f.maxPrice ?? ''}
          onChange={(e) => setF({ ...f, maxPrice: e.target.value ? Number(e.target.value) : undefined })}
        />
      </div>
      <PrimaryButton onClick={() => onApply(f)}>{t('filter.apply')}</PrimaryButton>
    </BottomSheet>
  );
}

/** Providers and agents: everyone else on Harmony, view only. */
export function OtherProvidersTab() {
  const t = useT();
  const { data, loading, more } = useProviders({});
  const open = useOpenProfile(false);
  return (
    <div className="pb-28 pt-2">
      {!data && loading && <Loading />}
      {data && !data.items.length && <EmptyState icon="👥" text={t('feed.empty')} />}
      <div className="grid grid-cols-2 gap-2 px-3">
        {data?.items.map((p) => (
          <ProviderTile key={p.id} p={p} onOpen={() => open(p)} />
        ))}
      </div>
      {data?.nextOffset !== null && data?.nextOffset !== undefined && <LoadMore loading={loading} onClick={more} />}
    </div>
  );
}

export function Loading() {
  return (
    <div className="flex justify-center py-20">
      <Preloader />
    </div>
  );
}

function LoadMore({ loading, onClick }: { loading: boolean; onClick: () => void }) {
  const t = useT();
  return (
    <div className="px-4 py-4">
      <PrimaryButton tone="soft" disabled={loading} onClick={onClick}>
        {loading ? <Preloader className="h-5 w-5" /> : t('common.load_more')}
      </PrimaryButton>
    </div>
  );
}

function RetryButton({ onClick }: { onClick: () => void }) {
  const t = useT();
  return (
    <button className="rounded-full bg-tg-button px-5 py-2 font-semibold text-tg-buttonText" onClick={onClick}>
      {t('common.retry')}
    </button>
  );
}
